/**
 * Sending somebody the family code, without this app sending anything.
 *
 * Three ways out, and all three hand the message to something the person
 * already has:
 *
 *   navigator.share  the OS share sheet -- their apps, their choice, and it
 *                    is the operating system making the request, not us
 *   Copy link        the clipboard, for everywhere the share sheet is not
 *   mailto:          their own mail client, pre-filled
 *
 * None of them is a fetch. There is no SES, no server-side send, and no
 * recipient's address stored anywhere -- the address is typed into somebody
 * else's mail app and never reaches this one. `tools/audit-network.py` reads
 * the built bundle and fails the publish if it can reach outside this app;
 * neither `navigator.share` nor a `mailto:` href is a URL it can find,
 * because the link is built at runtime from `window.location.origin`.
 *
 * The link is the point. A twenty-character code is not something anybody
 * should have to read aloud, and a link means the person receiving it types
 * nothing at all.
 */

import { useState } from 'react';

export function ShareActions({
  code,
  familyName,
  link,
  subject: asked,
  message,
  text = false,
}: {
  code: string;
  familyName: string;
  link: string;
  /** Overrides the family-code wording, for a child's own code. */
  subject?: string;
  /** The body of the email and text message, if not the family code's. */
  message?: string;
  /** Offer "Text it" (an sms: link) as well: the quick way to a child's phone. */
  text?: boolean;
}) {
  const [copied, setCopied] = useState<'link' | 'code' | null>(null);

  const subject =
    asked ?? (familyName ? `Join ${familyName} on Birbly` : 'Join my family on Birbly');
  const body =
    message ??
    `${subject}\n\n${link}\n\n` +
      `If the link does not work, open Birbly and enter this family code:\n` +
      `${code}\n`;

  async function copy(what: 'link' | 'code', text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      // Clipboard refused, or an insecure context. Both the code and the
      // link are on screen and can be selected by hand, so this is not worth
      // an error message.
    }
  }

  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  return (
    <div className="secret-actions">
      {canShare ? (
        <button
          type="button"
          className="text-button"
          onClick={() => {
            // Rejected when the person dismisses the sheet, which is not an
            // error -- it is them changing their mind.
            void navigator.share({ title: subject, text: subject, url: link }).catch(
              () => {},
            );
          }}
        >
          Share link
        </button>
      ) : null}

      <button type="button" className="text-button" onClick={() => void copy('link', link)}>
        {copied === 'link' ? 'Copied' : 'Copy link'}
      </button>

      <button type="button" className="text-button" onClick={() => void copy('code', code)}>
        {copied === 'code' ? 'Copied' : 'Copy code'}
      </button>

      {text ? (
        // `sms:?&body=` is the form both iOS and Android accept. Like
        // mailto:, it opens the person's own app; nothing is sent from here.
        <a className="text-button" href={`sms:?&body=${encodeURIComponent(body)}`}>
          Text it
        </a>
      ) : null}

      <a
        className="text-button"
        href={`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`}
      >
        Email it
      </a>
    </div>
  );
}
